import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { axiosInstance } from "../App";
import { CurrencyDisplay } from "./CurrencySettings";
import RevenueList from "./RevenueList";
import { 
  Calendar, 
  TrendingUp, 
  ChevronRight,
  FileText,
  Store,
  Receipt
} from "lucide-react";

const RevenueHistoryTabbed = ({ revenues: propRevenues, restaurants, onUpdate, user }) => {
  const [mainTab, setMainTab] = useState("last7days");
  const [selectedDay, setSelectedDay] = useState(null);
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownData, setDrilldownData] = useState(null);
  const [revenues, setRevenues] = useState(propRevenues || []);
  const [loadingRevenues, setLoadingRevenues] = useState(false);
  
  // Pagination for detailed report
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchAllRevenues = async () => {
    setLoadingRevenues(true);
    try {
      const response = await axiosInstance.get("/revenues");
      setRevenues(response.data);
    } catch (error) {
      console.error("Error fetching revenues:", error);
      setRevenues(propRevenues || []);
    } finally {
      setLoadingRevenues(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchAllRevenues();
    // Set default selected day to yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    setSelectedDay(formatDateKey(yesterday));
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get("/revenue-categories");
      setRevenueCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = revenueCategories.find(cat => cat.id === categoryId);
    return category ? category.name : "Unknown";
  };

  // Generate last 7 days
  const last7Days = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push({
        date: formatDateKey(date),
        label: formatDayLabel(date),
        dayName: getDayName(date),
        isToday: i === 0,
        isYesterday: i === 1
      });
    }
    return days;
  }, []);

  function formatDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function formatDayLabel(date) {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${day} ${month}`;
  }

  function getDayName(date) {
    return date.toLocaleString('en-US', { weekday: 'short' });
  }

  // Get revenues for selected day
  const dayRevenues = useMemo(() => {
    if (!selectedDay) return [];
    return revenues.filter(r => r.date === selectedDay);
  }, [revenues, selectedDay]);

  // Group by restaurant for selected day
  const restaurantBreakdown = useMemo(() => {
    const breakdown = {};
    dayRevenues.forEach(r => {
      if (!breakdown[r.restaurant_id]) {
        breakdown[r.restaurant_id] = {
          restaurant_id: r.restaurant_id,
          restaurant_name: r.restaurant_name,
          total: 0,
          entries: [],
          categories: {}
        };
      }
      breakdown[r.restaurant_id].total += r.total_amount || r.amount || 0;
      breakdown[r.restaurant_id].entries.push(r);
      
      // Aggregate categories
      if (r.amounts) {
        Object.entries(r.amounts).forEach(([catId, amount]) => {
          breakdown[r.restaurant_id].categories[catId] = 
            (breakdown[r.restaurant_id].categories[catId] || 0) + parseFloat(amount || 0);
        });
      }
    });
    
    // Convert to array and sort by total (highest first)
    return Object.values(breakdown).sort((a, b) => b.total - a.total);
  }, [dayRevenues]);

  const dayTotal = useMemo(() => {
    return dayRevenues.reduce((sum, r) => sum + (r.total_amount || r.amount || 0), 0);
  }, [dayRevenues]);

  const handleDrilldown = (restaurantData) => {
    setDrilldownData(restaurantData);
    setDrilldownOpen(true);
  };

  // Pagination for detailed report
  const paginatedRevenues = useMemo(() => {
    const sorted = [...revenues].sort((a, b) => {
      const dateCompare = b.date.localeCompare(a.date);
      if (dateCompare !== 0) return dateCompare;
      if (a.created_at && b.created_at) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      return 0;
    });
    
    const startIndex = (currentPage - 1) * pageSize;
    return sorted.slice(startIndex, startIndex + pageSize);
  }, [revenues, currentPage, pageSize]);

  const totalPages = Math.ceil(revenues.length / pageSize);

  return (
    <div className="space-y-6" data-testid="revenue-history-tabbed">
      {/* Main Tabs */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="last7days" className="gap-2" data-testid="tab-last7days">
            <Calendar className="w-4 h-4" />
            Last 7 Days
          </TabsTrigger>
          <TabsTrigger value="detailed" className="gap-2" data-testid="tab-detailed">
            <FileText className="w-4 h-4" />
            Detailed Report
          </TabsTrigger>
        </TabsList>

        {/* Last 7 Days Tab Content */}
        <TabsContent value="last7days" className="mt-6">
          {/* Day Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-2 mb-6">
            {last7Days.map((day) => (
              <Button
                key={day.date}
                variant={selectedDay === day.date ? "default" : "outline"}
                className={`flex-shrink-0 flex flex-col items-center px-4 py-3 h-auto ${
                  selectedDay === day.date 
                    ? "bg-blue-600 text-white" 
                    : "hover:bg-blue-50"
                }`}
                onClick={() => setSelectedDay(day.date)}
                data-testid={`day-tab-${day.date}`}
              >
                <span className="text-sm font-semibold">{day.label}</span>
                <span className="text-xs opacity-80">({day.dayName})</span>
              </Button>
            ))}
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-700" data-testid="day-total-revenue">
                      <CurrencyDisplay amount={dayTotal} />
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-xl">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Entries</p>
                    <p className="text-2xl font-bold text-blue-700" data-testid="day-entries-count">
                      {dayRevenues.length}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Receipt className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Restaurants</p>
                    <p className="text-2xl font-bold text-purple-700" data-testid="day-restaurants-count">
                      {restaurantBreakdown.length}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Store className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Restaurant Breakdown */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Restaurant-wise Breakdown</span>
                <Badge variant="secondary">Highest → Lowest</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {restaurantBreakdown.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No Revenue Entries</p>
                  <p className="text-sm mt-1">No revenue data recorded for this day</p>
                </div>
              ) : (
                restaurantBreakdown.map((item, index) => {
                  const percentage = dayTotal > 0 ? (item.total / dayTotal) * 100 : 0;
                  return (
                    <div
                      key={item.restaurant_id}
                      className="p-4 rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all"
                      onClick={() => handleDrilldown(item)}
                      data-testid={`restaurant-row-${item.restaurant_id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">
                            {index + 1}
                          </span>
                          <span className="font-semibold text-gray-900">{item.restaurant_name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-green-600">
                            <CurrencyDisplay amount={item.total} />
                          </span>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 w-16 text-right">
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Detailed Report Tab Content */}
        <TabsContent value="detailed" className="mt-6">
          <RevenueList 
            revenues={revenues} 
            restaurants={restaurants} 
            user={user}
            onUpdate={() => { onUpdate(); fetchAllRevenues(); }}
            pageSize={pageSize}
            currentPage={currentPage}
          />
          
          {/* Pagination Controls */}
          {revenues.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 p-4 bg-gray-50 rounded-xl">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, revenues.length)} of {revenues.length} entries
              </div>
              
              <div className="flex items-center gap-4">
                {/* Page Size Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <div className="flex gap-1">
                    {[10, 20, 50, 100].map((size) => (
                      <Button
                        key={size}
                        variant={pageSize === size ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setPageSize(size);
                          setCurrentPage(1);
                        }}
                        className="w-12"
                        data-testid={`page-size-${size}`}
                      >
                        {size}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Page Navigation */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    data-testid="page-first"
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    data-testid="page-prev"
                  >
                    Prev
                  </Button>
                  <span className="px-3 text-sm">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    data-testid="page-next"
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    data-testid="page-last"
                  >
                    Last
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Drilldown Dialog */}
      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent className="max-w-lg" data-testid="drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              {drilldownData?.restaurant_name}
            </DialogTitle>
            <p className="text-sm text-gray-500">
              Category Breakdown • {selectedDay && new Date(selectedDay).toLocaleDateString('en-IN', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric',
                weekday: 'short'
              })}
            </p>
          </DialogHeader>

          {drilldownData && (
            <div className="space-y-4">
              {/* Total */}
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-700">
                  <CurrencyDisplay amount={drilldownData.total} />
                </p>
              </div>

              {/* Category Breakdown */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Category-wise Breakdown</p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 font-medium">Category</th>
                        <th className="text-right p-3 font-medium">Amount</th>
                        <th className="text-right p-3 font-medium">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(drilldownData.categories)
                        .sort(([, a], [, b]) => b - a)
                        .map(([catId, amount]) => {
                          const share = drilldownData.total > 0 ? (amount / drilldownData.total) * 100 : 0;
                          return (
                            <tr key={catId} className="border-t">
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                                  {getCategoryName(catId)}
                                </div>
                              </td>
                              <td className="p-3 text-right font-medium">
                                <CurrencyDisplay amount={amount} />
                              </td>
                              <td className="p-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-blue-500 rounded-full"
                                      style={{ width: `${share}%` }}
                                    />
                                  </div>
                                  <span className="text-gray-500 w-12">{share.toFixed(1)}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                    <tfoot className="bg-gray-50 font-semibold">
                      <tr className="border-t-2">
                        <td className="p-3">Total</td>
                        <td className="p-3 text-right">
                          <CurrencyDisplay amount={drilldownData.total} />
                        </td>
                        <td className="p-3 text-right">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setDrilldownOpen(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevenueHistoryTabbed;
